import { Directive, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime } from 'rxjs/operators';

import { SavedReportViewService } from '../../services/saved-report-view/saved-report-view.service';
import {
  addFiltersToGetParams,
  addPropsToObject,
  hasUserAppliedFilters,
  propExists,
  SavedViewUrlParamName,
} from '../../utils';
import { SavedReportView, SavedReportViewReportType } from '../../models/saved-report-view';
import { SelectColumnsModalComponent } from './components/modals/select-columns-modal/select-columns-modal.component';
import {
  ViewSavedReportViewsModalComponent
} from './components/modals/saved-report-views/view-saved-report-views-modal/view-saved-report-views-modal.component';
import {
  DefaultReportViewScope,
  SetDefaultReportViewModalComponent
} from './components/modals/saved-report-views/set-default-report-view-modal/set-default-report-view-modal.component';
import {
  SimpleMessageModalComponent
} from '../../components/modals/simple-message-modal/simple-message-modal.component';
import {
  CreateSavedReportViewModalComponent
} from './components/modals/saved-report-views/create-saved-report-view-modal/create-saved-report-view-modal.component';
import {
  RenameSavedReportViewModalComponent
} from './components/modals/saved-report-views/rename-saved-report-view-modal/rename-saved-report-view-modal.component';
import { AccountService } from '../../services/account/account.service';
import { addToRecentlyViewedReports } from '../../utils-models';


export interface BaseFilter {
  [key: string]: any;
}


@Directive()
export abstract class ReportBaseDirective<TFilters extends BaseFilter> implements OnInit {
  public abstract filters: TFilters;

  protected abstract readonly DefaultColumns: string[];
  protected abstract readonly ReportColumnNames: string[];
  protected abstract readonly ColumnHelpText: {[key: string]: string};
  protected abstract readonly ReportColumnHeaderOverrides: {[key: string]: string};
  protected abstract readonly SavedViewExcludedProps: string[];
  protected abstract readonly ReportType: SavedReportViewReportType;
  /** Keys the loader seeds on every load; they only count as user filters when the URL actually carries them. */
  protected readonly SeededFilterProps: string[] = [];
  /** Keys whose null ("no filter") must survive a reload as an empty URL param. */
  protected readonly FiltersToSaveEmpty: string[] = [];

  public pageNumber = 1;

  public filtersChangedSubject = new Subject<null>();

  public columns: string[];

  public savedView: SavedReportView | null = null;
  public savedViewEdited = false;

  // The user's *personal* default view for this report, if any. This is what
  // the toolbar "Set as Default" / "Remove as Default" controls operate on, so
  // it must NOT include the organization default (PRO-1243).
  public defaultReportViewId: number | undefined;

  // The organization-wide default view for this report, if any (PRO-1243).
  // Used only as a fallback for auto-loading when the user has no personal
  // default; see effectiveDefaultReportViewId.
  public organizationDefaultReportViewId: number | undefined;

  public loadingSavedView = false;

  public successMessage = '';
  public errorMessage = '';

  public serverError: number = null;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected savedReportViewService: SavedReportViewService,
    protected accountService: AccountService,
    protected modalService: NgbModal,
  ) {}

  /**
   * The saved view that is auto-loaded on initial report load: the user's
   * personal default if set, otherwise the organization-wide default. The
   * personal default takes precedence (PRO-1243). This is distinct from
   * defaultReportViewId, which the toolbar's default controls act on.
   */
  public get effectiveDefaultReportViewId(): number | undefined {
    return this.defaultReportViewId ?? this.organizationDefaultReportViewId;
  }

  /**
   * Refresh the cached personal and organization default view IDs from the
   * current account. Called on init and after the user changes a default via
   * the Set Default View modal, so the toolbar's default badges stay accurate.
   */
  protected syncDefaultViewIdsFromAccount() {
    const myAccount = this.accountService.getMyAccount();
    this.defaultReportViewId =
      myAccount.default_report_view_ids?.[this.ReportType];
    this.organizationDefaultReportViewId =
      myAccount.organization_default_report_view_ids?.[this.ReportType];
  }

  ngOnInit() {
    this.syncDefaultViewIdsFromAccount();

    this.columns = [...this.DefaultColumns];

    // Handle changes to the payment filters by reloading the payments,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadReport();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute,
        this.FiltersToSaveEmpty
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = this.loadFiltersFromGetParams(this.activatedRoute);
    this.setInitialFilterParams(false);
    if (!this.urlCarriesUserFilters()) {
      const initialQPs = this.activatedRoute.snapshot.queryParamMap;
      if (initialQPs.get(SavedViewUrlParamName)) {
        const parsedSrvID = parseInt(
          initialQPs.get(SavedViewUrlParamName),
          10
        );
        if (!isNaN(parsedSrvID)) {
          this.loadSavedView(parsedSrvID);
        }
      } else if (propExists(this.effectiveDefaultReportViewId)) {
        this.loadSavedView(this.effectiveDefaultReportViewId);
      }
    } else {
      this.savedViewEdited = true;
    }

    this.loadReport();

    addToRecentlyViewedReports(this.ReportType);
  }

  /** Whether the filters loaded from the URL go beyond what the page seeds, which blocks a saved view from auto-loading. */
  protected urlCarriesUserFilters(): boolean {
    return hasUserAppliedFilters(
      this.filters,
      this.activatedRoute.snapshot.queryParamMap,
      this.SavedViewExcludedProps,
      this.SeededFilterProps,
    );
  }

  /**
   * Handle a change in the report filters. The list of payments will be
   * filtered according to the new values.
   */
  public handleFilterChange() {
    this.pageNumber = 1;

    this.savedViewEdited = true;

    this.filtersChangedSubject.next();
  }

  /**
   * Update the current page number to the given value and load the page of
   * payments.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadReport();
  }

  public openSelectReportColumnsModal() {
    const modalRef = this.modalService.open(
      SelectColumnsModalComponent,
      {
        size: 'lg',
      }
    );
    modalRef.componentInstance.initialColumns = this.columns;
    modalRef.componentInstance.defaultColumns = this.DefaultColumns;
    modalRef.componentInstance.columnOptions = this.ReportColumnNames;
    modalRef.componentInstance.columnHelpText = this.ColumnHelpText;
    modalRef.componentInstance.columnHeaderOverrides = this.ReportColumnHeaderOverrides;
    modalRef.componentInstance.savedCallback = (newColumns: string[]) => {
      this.columns = newColumns;
      this.savedViewEdited = true;
      this.handleColumnsChange();
    };
  }

  /**
   * Handle a change to the selected columns from the column picker. Reports
   * whose request depends on the shown columns (the members report's billing
   * computation, PRO-1367) override this to refetch. The saved-view paths
   * call `loadReport()` themselves once the new columns are in place.
   */
  protected handleColumnsChange(): void {}

  public openSavedViewsModal() {
    const modalRef = this.modalService.open(ViewSavedReportViewsModalComponent);
    modalRef.componentInstance.reportType = this.ReportType;
    modalRef.componentInstance.savedViewEdited = this.savedViewEdited;
    modalRef.componentInstance.savedViewSelectedCallback = (srv: SavedReportView | null) => {
      this.savedView = srv;
      this.savedViewEdited = false;

      this.setInitialFilterParams(true);

      if (this.savedView === null) {
        this.columns = [...this.DefaultColumns];

        this.router.navigate(
          [],
          {
            relativeTo: this.activatedRoute,
            queryParams: {},
            replaceUrl: true
          }
        );
      } else {
        this.columns = [...srv.columns];
        addPropsToObject(
          srv.filters,
          this.filters
        );

        this.router.navigate(
          [],
          {
            relativeTo: this.activatedRoute,
            queryParams: {[SavedViewUrlParamName]: srv.id},
            replaceUrl: true
          }
        );
      }

      this.loadReport();
    }
    modalRef.componentInstance.createNewSavedViewCallback = () => {
      this.openCreateSavedViewModal();
    }
    modalRef.componentInstance.setPersonalDefaultCallback = () => {
      this.openSetDefaultModal('personal');
    }
    modalRef.componentInstance.setOrganizationDefaultCallback = () => {
      this.openSetDefaultModal('organization');
    }
  }

  public openSetDefaultModal(scope: DefaultReportViewScope) {
    const modalRef = this.modalService.open(SetDefaultReportViewModalComponent);
    modalRef.componentInstance.reportType = this.ReportType;
    modalRef.componentInstance.scope = scope;

    // Re-read the default IDs once the modal is closed (or dismissed) so the
    // toolbar's default badges reflect any change the user just made.
    const resync = () => this.syncDefaultViewIdsFromAccount();
    modalRef.result.then(resync, resync);
  }

  public openCreateSavedViewModal() {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(CreateSavedReportViewModalComponent);
    modalRef.componentInstance.reportType = this.ReportType;
    modalRef.componentInstance.columns = [...this.columns];
    modalRef.componentInstance.filters = {...this.filters};
    for (const excludedProp of this.SavedViewExcludedProps) {
      modalRef.componentInstance.filters[excludedProp] = undefined;
    }
    modalRef.componentInstance.savedCallback = (srv: SavedReportView) => {
      this.savedView = srv;
      this.savedViewEdited = false;
      this.successMessage = 'Saved view created.';

      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams: {[SavedViewUrlParamName]: srv.id},
          replaceUrl: true
        }
      );
    }
  }

  public openRenameSavedViewModal() {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(RenameSavedReportViewModalComponent);
    modalRef.componentInstance.srv = this.savedView;
    modalRef.componentInstance.savedCallback = (srv: SavedReportView) => {
      this.savedView = srv;
      this.successMessage = 'Saved view renamed.';

      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams: {[SavedViewUrlParamName]: srv.id},
          replaceUrl: true
        }
      );
    }
  }

  public updateSavedView() {
    this.loadingSavedView = true;
    this.successMessage = '';
    this.errorMessage = '';

    const newFilters: any = {...this.filters};
    for (const excludedProp of this.SavedViewExcludedProps) {
      newFilters[excludedProp] = undefined;
    }

    this.savedReportViewService
      .updateSavedReportViewColumnsAndFilters(
        this.savedView,
        [...this.columns],
        newFilters
      )
      .then(srv => {
        this.savedView = srv;
        this.savedViewEdited = false;
        this.successMessage = 'Saved view updated.';

        this.router.navigate(
          [],
          {
            relativeTo: this.activatedRoute,
            queryParams: {[SavedViewUrlParamName]: srv.id},
            replaceUrl: true
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          reason.error.text().then(text => { this.errorMessage = text; });
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this view.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSavedView = false; });
  }

  public deleteSavedView() {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Saved View';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete this saved view?';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSavedView = true;
      this.savedReportViewService.deleteSavedReportView(this.savedView)
        .then(() => {
          this.successMessage = 'Saved view deleted.';

          this.savedView = null;
          this.savedViewEdited = false;

          this.setInitialFilterParams(true);
          this.columns = [...this.DefaultColumns];

          this.router.navigate(
            [],
            {
              relativeTo: this.activatedRoute,
              queryParams: {},
              replaceUrl: true
            }
          );

          this.loadReport();
        })
        .catch(reason => {
          if (reason.status === 400) {
            reason.error.text().then(text => { this.errorMessage = text; });
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this view.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSavedView = false; });
    }
  }

  protected loadSavedView(savedViewId: number) {
    this.loadingSavedView = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.savedReportViewService
      .getSavedReportView(savedViewId)
      .then(srv => {
        if (srv.report_type !== this.ReportType) {
          return;
        }
        this.savedView = srv;
        this.savedViewEdited = false;
        this.columns = [...srv.columns];
        addPropsToObject(
          srv.filters,
          this.filters
        );
        this.loadReport();
      })
      .catch(reason => {
        // The view may have been deleted (e.g. a stale personal/org default or
        // a ?saved_view_id link to a removed view). Fall back to the default
        // columns and filters rather than surfacing an error — the report is
        // still loaded by the caller. Only unexpected errors are surfaced.
        if (reason.status !== 404) {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSavedView = false; });
  }

  public abstract loadReport(): void;
  protected abstract loadFiltersFromGetParams(activatedRoute: ActivatedRoute): TFilters;
  protected abstract setInitialFilterParams(replaceFilters: boolean): void;
}
